import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeafletMap from "@/components/LeafletMap";
import VehiclesCarousel from "@/components/VehiclesCarousel";
import axios from "axios";
import { Star } from "lucide-react";
import DateTimeQuarterInput from "@/components/DateTimeQuarterInput";

export default function Home() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [offices, setOffices] = useState([]);
  const [office, setOffice] = useState(null);
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [officeVehicles, setOfficeVehicles] = useState([]);

  useEffect(() => {
    axios.get("/api/ubicaciones").then(r => {
      const data = r.data || [];
      setOffices(data);
      if (data.length > 0) setOffice(data[0]);
    }).catch(() => {});
    axios.get("/api/testimonials").then(r => setTestimonials(r.data || [])).catch(() => {});
    axios.get("/api/vehicles?visible_only=true").then(r => setOfficeVehicles(r.data || [])).catch(() => {});
  }, []);

  const vehicleMarkers = office
    ? officeVehicles
        .filter(v => v.ubicacion_id === office.id && Number.isFinite(Number(v.latitud)) && Number.isFinite(Number(v.longitud)))
        .map(v => ({
          lat: v.latitud,
          lng: v.longitud,
          label: v.marca_modelo,
          price: (v.precio_dia === null || v.precio_dia === undefined || v.precio_dia === "" || !Number.isFinite(Number(v.precio_dia))) ? null : `${Number(v.precio_dia).toFixed(2)} €/día`,
        }))
    : [];

   const dateOrderError = Boolean(
    pickup && dropoff && new Date(dropoff) <= new Date(pickup)
 );

  const onSearch = (e) => {
    e.preventDefault();
    if (!office) return;
    if (dateOrderError) { return; }
    const qs = new URLSearchParams({ office: office.id, pickup, dropoff }).toString();
    nav(`/vehiculos?${qs}`);
  };

  return (
    <>
      {/* HERO */}
      <section className="hero-light-bg relative overflow-hidden hero-stripe hero-stripe-light" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-12">
          <div className="mb-10 animate-slidein">
            <div className="divider-red mb-6"></div>
            <h1 className="slogan text-black text-5xl sm:text-7xl lg:text-8xl">
              {t("home.heroLinea1")}<br/>
              {t("home.heroLinea2")}<br/>
              <span className="text-[var(--pc-red)]">{t("home.heroLinea3")}</span>
            </h1>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-neutral-900 p-5 sm:p-7 rounded-lg border border-neutral-800" data-testid="search-form-container">
            <form onSubmit={onSearch} className="space-y-3 text-white">
              <label className="text-xs uppercase tracking-widest text-neutral-400">{t("home.lugarRecogida")}</label>
              <select
                className="pc-input text-black"
                value={office?.id || ""}
                onChange={(e) => setOffice(offices.find(o => o.id === e.target.value) || offices[0])}
                data-testid="search-office"
              >
                {offices.map(o => <option key={o.id} value={o.id}>{o.nombre_zona}</option>)}
              </select>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400">{t("home.recogida")}</label>
                  <DateTimeQuarterInput value={pickup} onChange={setPickup} placeholder={t("home.placeholderRecogida")} dateTestId="search-pickup-date" timeTestId="search-pickup-time" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400">{t("home.devolucion")}</label>
                  <DateTimeQuarterInput value={dropoff} onChange={setDropoff} min={pickup || undefined} placeholder={t("home.placeholderDevolucion")} dateTestId="search-dropoff-date" timeTestId="search-dropoff-time" />
                </div>
              </div>
              {dateOrderError && (
                 <div className="text-sm bg-red-100 text-red-700 px-3 py-2 rounded border border-red-300" data-testid="home-date-order-error">
                   {t("home.errorFechas")}
                 </div>
              )}

              <button type="submit" className="btn-red w-full py-3 mt-2" data-testid="search-submit">{t("home.buscarCoches")}</button>
            </form>
            <div className="rounded-lg overflow-hidden">
              <LeafletMap
                markers={vehicleMarkers.length > 0 ? vehicleMarkers : [{ lat: office?.latitud || 40.3662, lng: office?.longitud || -3.5937, label: office?.nombre_zona || "" }]}
                height={300}
              />
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="section-light py-16" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <div className="divider-red mb-4"></div>
            <h2 className="font-display text-4xl sm:text-5xl">{t("home.comoFunciona")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "/como-funciona-1.webp",
                "/como-funciona-2.webp",
                "/como-funciona-3.webp",
                "/como-funciona-4.webp",
              ].map((src, i) => (
                <div key={`step-${i+1}`} className="rounded-lg overflow-hidden" data-testid={`step-${i+1}`}>
                  <img src={src} alt={t("home.paso", { numero: i + 1 })} className="w-full h-auto object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CARRUSEL FLOTA */}
      <VehiclesCarousel />

      {/* TESTIMONIALS */}
      <section className="section-grey py-16" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <div className="divider-red mb-4"></div>
            <h2 className="font-display text-4xl sm:text-5xl">{t("home.opinionesClientes")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((item) => (
              <div key={item.id} className="bg-white p-6 rounded-lg border border-neutral-200" data-testid={`testimonial-${item.id}`}>
                <div className="flex gap-1 mb-3">
                  {[...Array(item.estrellas || 5)].map((_, i) => (
                    <Star key={i} size={18} fill="#FFC107" stroke="#FFC107" />
                  ))}
                </div>
                <div className="text-xs text-neutral-500 mb-1">{item.fecha}</div>
                <div className="font-display text-xl mb-2">{item.nombre}</div>
                <p className="text-sm text-neutral-700 leading-relaxed">{item.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
